import React from "react";

type CommonProps = {
  id: string;
  label: string;
  className?: string;
  helpText?: string;
  disabled?: boolean;
};

export function DateField(props: CommonProps & {
  value: string;
  onChange: (v: string) => void;
}) {
  const { id, label, value, onChange, className, helpText, disabled } = props;
  return (
    <div className={className ?? ""}>
      <label className="form-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        type="date"
        className="form-control"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.currentTarget.value)}
        aria-describedby={helpText ? `${id}-help` : undefined}
      />
      {helpText && <div id={`${id}-help`} className="form-text">{helpText}</div>}
    </div>
  );
}

export function NumberField(props: CommonProps & {
  value: number;
  onChange: (v: number) => void;
  step?: number | "any";
  placeholder?: string;
}) {
  const { id, label, value, onChange, className, helpText, disabled, step, placeholder } = props;

  const [draft, setDraft] = React.useState<string>(() => String(value));

  React.useEffect(() => {
    if (draft !== "" && Number(draft) !== value) {
      setDraft(String(value));
    }
  }, [value]);

  return (
    <div className={className ?? ""}>
      <label className="form-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        className="form-control"
        value={draft}
        step={step ?? "any"}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.currentTarget.value;
          setDraft(raw);

          if (raw === "") {
            return;
          }
          const n = Number(raw);
          if (Number.isFinite(n)) {
            onChange(n);
          }
        }}
        onBlur={() => {
          if (draft === "") {
            setDraft("0");
            onChange(0);
          }
        }}
        aria-describedby={helpText ? `${id}-help` : undefined}
      />
      {helpText && <div id={`${id}-help`} className="form-text">{helpText}</div>}
    </div>
  );
}


export function ReadonlyNumberField(props: CommonProps & {
  value: number | string;
}) {
  const { id, label, value, className, helpText, disabled } = props;
  return (
    <div className={className ?? ""}>
      <label className="form-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        className="form-control"
        value={value ?? ""}
        readOnly
        disabled={disabled ?? true}
        aria-describedby={helpText ? `${id}-help` : undefined}
      />
      {helpText && <div id={`${id}-help`} className="form-text">{helpText}</div>}
    </div>
  );
}

/** Näyttää %:na mutta antaa ylös-arvona murto-osan (esim. 0.08). */
export function PercentField(props: CommonProps & {
  value: number | null | undefined;     // esim. 0.08
  onChange: (fraction: number) => void; // parent saa aina numeron
  step?: number | "any";
  placeholder?: string;
}) {
  const { id, label, value, onChange, className, helpText, disabled, step, placeholder } = props;

  const toPctString = (v?: number | null) => {
    if (v == null || !Number.isFinite(v)) return "";
    const pct = Math.round((v * 100) * 100) / 100;
    return String(pct);
  };

  const fromPct = (s: string): number | null => {
    if (s.trim() === "") return null;
    const n = Number(s);
    return Number.isFinite(n) ? n / 100 : null;
  };

  const [draft, setDraft] = React.useState<string>(() => toPctString(value));

  React.useEffect(() => {
    if (draft !== "" && fromPct(draft) !== value) {
      setDraft(toPctString(value));
    }
  }, [value]);

  return (
    <div className={className ?? ""}>
      <label className="form-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        className="form-control"
        step={step ?? "any"}
        placeholder={placeholder}
        disabled={disabled}
        value={draft}
        onChange={(e) => {
          const raw = e.currentTarget.value;
          setDraft(raw);

          const f = fromPct(raw);
          if (f != null) {
            onChange(f);
          }
        }}
        onBlur={() => {
          if (draft === "") {
            setDraft("0");
            onChange(0); // tulkitaan tyhjä -> 0%
          }
        }}
        aria-describedby={helpText ? `${id}-help` : undefined}
      />
      {helpText && <div id={`${id}-help`} className="form-text">{helpText}</div>}
    </div>
  );
}

export type Option<T extends string> = { value: T; label: string };

export function SelectField<T extends string>(props: CommonProps & {
  value: T;
  onChange: (v: T) => void;
  options: Option<T>[];
}) {
  const { id, label, value, onChange, options, className, helpText, disabled } = props;
  return (
    <div className={className ?? ""}>
      <label className="form-label" htmlFor={id}>{label}</label>
      <select
        id={id}
        className="form-select"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.currentTarget.value as T)}
        aria-describedby={helpText ? `${id}-help` : undefined}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {helpText && <div id={`${id}-help`} className="form-text">{helpText}</div>}
    </div>
  );
}
